import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Flex } from '@mantine/core';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useQuery } from '@tanstack/react-query';
import instance from '@jadwalh/axios';
import { Input } from '../input';
import Select from 'react-select';
import ReactCountryFlag from 'react-country-flag';
import { PhoneWrapper } from './style';

const FORM_REGEX_VALIDATORS = {
  numbersOnly: /^[0-9]*$/g,
  validPhoneNumber: /^\(?\d{2,4}\)?[\s.-]?\d{3}[\s.-]?\d{3,4}$/g,
};
interface IProps {
  codeInputName?: string;
  numInputName?: string;
  label?: string;
  phoneNumberRules?: RegisterOptions;
  codeRequired?: boolean;
  isAdding?: boolean;
  onCodeChange?: (value: string) => void;
  onNumberChange?: (value: string) => void;
  numberDefaultValue?: string;
  codeDefaultValue?: number;
  moduleName: string;
  phonePlaceholder?: string;
  showPlaceholder?: boolean;
  readonly?: boolean;
  isRequired?: boolean;
}

export function Phone({
  codeInputName = 'phone_code',
  numInputName = 'phone_number',
  label,
  codeRequired = true,
  phoneNumberRules = undefined,
  isAdding = true,
  onCodeChange,
  onNumberChange,
  numberDefaultValue,
  codeDefaultValue,
  moduleName,
  phonePlaceholder,
  showPlaceholder = true,
  readonly = false,
  isRequired = true,
  ...rest
}: IProps) {
  interface ipDataItems {
    country_code: string;
    country_name: string;
    city: string;
    IPv4: string;
  }

  const { formState, watch, setValue, control } = useFormContext();
  const { errors } = formState;
  const { formatMessage } = useIntl();
  const [selectedCode, setSelectedCode] = useState<any>();
  const [ipData, setIpData] = useState<any>();
  const [ipApiCompleted, setIpApiCompleted] = useState(false);
  const [matches, setMatches] = useState(
    window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    window
      .matchMedia('(min-width: 768px)')
      .addEventListener('change', (e) => setMatches(e.matches));
  }, []);

  const fetchIpData = () => {
    fetch('https://ipapi.co/json/')
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        setIpData(data);
        localStorage.setItem('ipData', JSON.stringify(data));
        setIpApiCompleted(true);
      })
      .catch(function (error) {
        setIpData(null);
        setIpApiCompleted(true);
      });
  };

  useEffect(() => {
    if (!localStorage.getItem('ipData')) {
      fetchIpData();
    } else {
      setIpData(JSON.parse(localStorage.getItem('ipData') || '{}'));
      setIpApiCompleted(true);
    }
  }, []);

  const { data: countriesPhoneCode = [] } = useQuery({
    queryKey: ['countryData'],
    queryFn: () => instance.get('/app/countries'),
    select: (
      res: any
    ): { value: string; label: string; country_code: string }[] =>
      res.data.data.map((item: any, index: number) => ({
        value: item.phone_code,
        label: (
          <div>
            <ReactCountryFlag countryCode={item.code_alpha_2} svg />
            {'  '}({item.phone_code}+) {item.name}
          </div>
        ),
        country_phone_code: item.country_phone_code_label,
        country_code: item.code_alpha_2,
        key: `${item.phone_code}-${moduleName}-${index}`,
      })),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isAdding && countriesPhoneCode.length > 0 && !watch(codeInputName) && ipApiCompleted) {
      let countryPhoneCode: any;

      if (ipData?.country_code) {
        // Try to use detected country
        countryPhoneCode = countriesPhoneCode.find(
          (country) => country.country_code === ipData.country_code
        );
      }

      // Only fallback to Saudi Arabia if IP API completed and failed (ipData is null) or country not found
      if (!countryPhoneCode && ipData === null) {
        countryPhoneCode = countriesPhoneCode.find(
          (country) => country.country_code === 'SA'
        );
      }

      if (countryPhoneCode) {
        setSelectedCode(countryPhoneCode);
        setValue(codeInputName, countryPhoneCode.value || '');
      }
    }
  }, [ipData, ipApiCompleted, isAdding, codeInputName, countriesPhoneCode, setValue]);

  useEffect(() => {
    if (
      watch(codeInputName) &&
      countriesPhoneCode.length > 0 &&
      watch(codeInputName) != selectedCode?.value
    ) {
      const matchedCode: any = countriesPhoneCode.find(
        (country) => country.value == watch(codeInputName)
      );
      if (matchedCode) {
        setSelectedCode(matchedCode);
        setValue(codeInputName, matchedCode.value);
      }
    }
  }, [watch(codeInputName), countriesPhoneCode]);

  return (
    <PhoneWrapper className="phone-input">
      <Flex direction="column">
        <label>
          {label && (
            <p>
              {label} {codeRequired && <span>*</span>}
            </p>
          )}
        </label>
        <Flex direction="row" pl={0}>
          <Input
            className="phone_number"
            placeholder={
              showPlaceholder
                ? phonePlaceholder || formatMessage({ id: 'CustomerPhone' })
                : ''
            }
            name={numInputName}
            onChange={(event) => {
              setValue(numInputName, event.target.value);
              onNumberChange && onNumberChange(event.target.value);
            }}
            defaultValue={numberDefaultValue}
            type="tel"
            readonly={readonly}
            registerOptions={
              readonly || !isRequired
                ? undefined
                : phoneNumberRules || {
                    required: {
                      value: true,
                      message: formatMessage({ id: 'requiredField' }),
                    },
                    pattern: {
                      value: FORM_REGEX_VALIDATORS.validPhoneNumber,
                      message: formatMessage({
                        id: 'phoneInvalidFormat',
                      }),
                    },
                  }
            }
          />
          <Controller
            control={control}
            name={codeInputName}
            defaultValue={codeDefaultValue}
            render={({ field: { onChange, value } }) => (
              <Select
                className={`code-input ${
                  errors[codeInputName] ? 'hasError' : ''
                }`}
                isDisabled={readonly}
                getOptionValue={(option) => option.country_phone_code}
                classNamePrefix="react-select"
                name={codeInputName}
                placeholder={formatMessage({ id: 'phone_code' })}
                options={countriesPhoneCode || []}
                noOptionsMessage={() => formatMessage({ id: 'noData' })}
                onChange={(selected: any) => {
                  setSelectedCode(selected);
                  setValue(codeInputName, selected.value.toString());
                  onChange(selected.value);
                  onCodeChange && onCodeChange(selected);
                }}
                value={selectedCode}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    width: '220px',
                    zIndex: 9999,
                  }),
                }}
              />
            )}
          />
        </Flex>
        {!!errors && (!!errors[codeInputName] || !!errors[numInputName]) && (
          <div className="ErrorMessageStyle phoneErrorMsg">
            <ErrorMessage
              errors={errors}
              name={codeInputName || numInputName}
              render={({ message }) => <p>{message}</p>}
            />
          </div>
        )}
      </Flex>
    </PhoneWrapper>
  );
}
